import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle, Shield, DollarSign, Activity, RefreshCw, ChevronRight, Filter } from 'lucide-react';
import api from '../../lib/api';

const SEVERITY_COLORS = {
  CRITICAL: 'bg-red-600 text-white',
  HIGH: 'bg-orange-500 text-white',
  MEDIUM: 'bg-yellow-500 text-black',
  LOW: 'bg-slate-400 text-white',
};

const TYPE_ICONS = {
  operational: Activity,
  financial: DollarSign,
  compliance: Shield,
};

const TYPE_COLORS = {
  operational: 'text-blue-400',
  financial: 'text-emerald-400',
  compliance: 'text-purple-400',
};

export default function SmartAlertsPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterType, setFilterType] = useState('');

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/smart-alerts/dashboard');
      if (res.data?.data) setDashboard(res.data.data);
    } catch (e) { console.error(e); }
  };

  const fetchAlerts = async () => {
    try {
      const params = new URLSearchParams({ page_size: '50' });
      if (filterStatus) params.set('status', filterStatus);
      if (filterSeverity) params.set('risk_level', filterSeverity);
      if (filterType) params.set('alert_type', filterType);
      const res = await api.get(`/smart-alerts/list?${params}`);
      if (res.data?.data) {
        setAlerts(res.data.data.items || []);
        setTotal(res.data.data.total || 0);
      }
    } catch (e) { console.error(e); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      await api.post('/smart-alerts/scan');
      await fetchDashboard();
      await fetchAlerts();
    } catch (e) { console.error(e); }
    setScanning(false);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchDashboard();
      await fetchAlerts();
      setLoading(false);
    };
    load();
  }, [filterStatus, filterSeverity, filterType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const sev = dashboard?.by_severity || {};
  const typ = dashboard?.by_type || {};

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="smart-alerts-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Smart Alerts</h1>
            <p className="text-sm text-slate-400 mt-1">AI-powered risk monitoring and early detection</p>
          </div>
          <Button
            onClick={runScan}
            disabled={scanning}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="scan-alerts-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning...' : 'Run Scan'}
          </Button>
        </div>

        {/* Severity Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(level => (
            <Card
              key={level}
              className="bg-[#12121a] border-[#1e1e2e] cursor-pointer hover:border-slate-600 transition-colors"
              onClick={() => { setFilterSeverity(filterSeverity === level ? '' : level); }}
              data-testid={`severity-card-${level.toLowerCase()}`}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">{level}</p>
                  <p className="text-2xl font-bold mt-1">{sev[level] || 0}</p>
                </div>
                <Badge className={`${SEVERITY_COLORS[level]} text-xs px-2`}>{level}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Type Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {['operational', 'financial', 'compliance'].map(atype => {
            const Icon = TYPE_ICONS[atype];
            return (
              <Card
                key={atype}
                className="bg-[#12121a] border-[#1e1e2e] cursor-pointer hover:border-slate-600 transition-colors"
                onClick={() => { setFilterType(filterType === atype ? '' : atype); }}
                data-testid={`type-card-${atype}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${TYPE_COLORS[atype]}`} />
                  <div>
                    <p className="text-xs text-slate-500 capitalize">{atype}</p>
                    <p className="text-lg font-semibold">{typ[atype] || 0} active</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="bg-[#12121a] border border-[#1e1e2e] rounded px-3 py-1.5 text-sm text-slate-300"
            data-testid="filter-status"
          >
            <option value="">All Active</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
          </select>
          <select
            value={filterSeverity}
            onChange={e => setFilterSeverity(e.target.value)}
            className="bg-[#12121a] border border-[#1e1e2e] rounded px-3 py-1.5 text-sm text-slate-300"
            data-testid="filter-severity"
          >
            <option value="">All Severity</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="bg-[#12121a] border border-[#1e1e2e] rounded px-3 py-1.5 text-sm text-slate-300"
            data-testid="filter-type"
          >
            <option value="">All Types</option>
            <option value="operational">Operational</option>
            <option value="financial">Financial</option>
            <option value="compliance">Compliance</option>
          </select>
          {(filterStatus || filterSeverity || filterType) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(''); setFilterSeverity(''); setFilterType(''); }} className="text-slate-400 text-xs">
              Clear filters
            </Button>
          )}
        </div>

        {/* Alert List */}
        <Card className="bg-[#12121a] border-[#1e1e2e]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-300">
              Alerts ({total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {alerts.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No alerts match current filters
              </div>
            ) : (
              <div className="divide-y divide-[#1e1e2e]">
                {alerts.map(alert => {
                  const Icon = TYPE_ICONS[alert.alert_type] || Activity;
                  return (
                    <div
                      key={alert.id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#1a1a25] cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/smart-alerts/${alert.id}`)}
                      data-testid={`alert-row-${alert.id}`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${TYPE_COLORS[alert.alert_type]}`} />
                      <Badge className={`${SEVERITY_COLORS[alert.risk_level]} text-[10px] px-1.5 shrink-0`}>
                        {alert.risk_level}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-200 truncate">{alert.title}</p>
                        <p className="text-xs text-slate-500 truncate">{alert.reason}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-700 shrink-0">
                        {alert.status}
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-slate-600 shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
